import { cn } from '@/lib/utils';
import { Icon, LoadingRing } from '@pms-digital/design-system';
import { LONGUEUR_MAX_TEXTE } from '@rosetta/shared';
import { type DragEvent, useId, useLayoutEffect, useRef, useState } from 'react';
import { useApresDelai } from './use-apres-delai';

interface ZoneTexteProps {
  valeur: string;
  onChange: (valeur: string) => void;
  /** Locked while a translation is running: no typing, no drop. */
  verrouille: boolean;
  /** The last file could not be loaded: this message is shown under the zone. */
  erreurFichier: string | null;
  /** Name of the file being read, `null` when none is. Announced after 300 ms (S13). */
  lecture: string | null;
  /** A file was dropped on the zone. */
  onFichierDepose: (fichier: File) => void;
  /** The length of the text in characters (`longueurTexte`), for the counter (#13). */
  longueur: number;
}

/** From this many characters the counter shows (#13): the limit is near. */
export const SEUIL_COMPTEUR = 9_000;

/** Said under the zone while the text is longer than a translation may be (#13). */
export const MESSAGE_TEXTE_TROP_LONG_ZONE =
  'Ce texte dépasse 10 000 caractères (environ 3 pages) : raccourcissez-le pour le traduire.';

/** "9 412", with the thin no-break space French writes between the thousands. */
const nombre = (valeur: number): string => valeur.toLocaleString('fr-FR');

/** Does the browser size a textarea by its content (`field-sizing: content`) by itself? */
function gereFieldSizing(): boolean {
  return typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content') === true;
}

/** Only a drag that carries files is ours; a dragged text selection keeps its default. */
function porteDesFichiers(evenement: DragEvent): boolean {
  return Array.from(evenement.dataTransfer.types).includes('Files');
}

/**
 * The text zone of the input screen: 360 px minimum, grows with its text and never
 * scrolls (the page does). Empty, it shows a dashed frame and the two equal gestures
 * (write or drop a file); a file dragged over it switches to the drop state; with
 * text, a reminder says a file can still be dropped. While a file is read the zone takes
 * no text and no drop, and, if the read lasts more than 300 ms, says so (S13). The error
 * of a file is announced under the zone.
 *
 * The length (#13): from 9 000 characters a counter shows under the zone, on the right
 * ("9 412 / 10 000 caractères"); beyond 10 000 it turns `rouge-deep` and a sentence says the text
 * must be shortened. Both describe the zone (`aria-describedby`) rather than speak at each
 * keystroke: the footer, a polite live region, says once why "traduire" is off.
 */
export function ZoneTexte({
  valeur,
  onChange,
  verrouille,
  erreurFichier,
  lecture,
  onFichierDepose,
  longueur,
}: ZoneTexteProps) {
  const refZone = useRef<HTMLTextAreaElement>(null);
  const [survol, setSurvol] = useState(false);
  const idAide = useId();
  const idErreur = useId();
  const idLecture = useId();
  const idCompteur = useId();
  const idTropLong = useId();
  const enLecture = lecture !== null;
  const lectureAffichee = useApresDelai(enLecture);
  const bloque = verrouille || enLecture;
  const compteur = longueur >= SEUIL_COMPTEUR;
  const tropLong = longueur > LONGUEUR_MAX_TEXTE;

  // Fallback for the browsers without `field-sizing`: measure the content and set the
  // height by hand. `valeur` is the dependency on purpose, the height follows the text.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useLayoutEffect(() => {
    const zone = refZone.current;
    if (zone === null || gereFieldSizing()) return;
    zone.style.height = 'auto';
    const bordures = zone.offsetHeight - zone.clientHeight;
    zone.style.height = `${zone.scrollHeight + bordures}px`;
  }, [valeur]);

  const vide = valeur === '';

  function surDragEntrant(evenement: DragEvent) {
    if (!porteDesFichiers(evenement)) return;
    // Always claim a file drag over the zone: unhandled, the browser would open the file.
    evenement.preventDefault();
    if (!bloque) setSurvol(true);
  }

  function surDragSortant(evenement: DragEvent<HTMLDivElement>) {
    // Moving over a child (the textarea) fires a leave on the wrapper: ignore it.
    if (evenement.currentTarget.contains(evenement.relatedTarget as Node | null)) return;
    setSurvol(false);
  }

  function surDepot(evenement: DragEvent) {
    if (!porteDesFichiers(evenement)) return;
    evenement.preventDefault();
    setSurvol(false);
    if (bloque) return;
    const fichier = evenement.dataTransfer.files[0];
    if (fichier !== undefined) onFichierDepose(fichier);
  }

  const decrit = [
    vide && !lectureAffichee ? idAide : null,
    lectureAffichee ? idLecture : null,
    erreurFichier !== null ? idErreur : null,
    tropLong ? idTropLong : null,
    compteur ? idCompteur : null,
  ]
    .filter((id) => id !== null)
    .join(' ');

  return (
    <>
      <div
        className="relative mt-4"
        onDragEnter={surDragEntrant}
        onDragOver={surDragEntrant}
        onDragLeave={surDragSortant}
        onDrop={surDepot}
      >
        <textarea
          ref={refZone}
          aria-label="texte à traduire"
          aria-describedby={decrit === '' ? undefined : decrit}
          value={valeur}
          disabled={verrouille}
          readOnly={enLecture}
          aria-busy={enLecture}
          onChange={(evenement) => onChange(evenement.target.value)}
          className={cn(
            'field-sizing-content block min-h-[360px] w-full resize-none overflow-hidden rounded-md border-2 p-4 font-light text-black text-body leading-[1.6]',
            vide || survol ? 'border-dashed' : 'border-solid',
            lectureAffichee && 'cursor-wait',
            verrouille
              ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-700'
              : survol
                ? 'border-bleu-nuit bg-gray-50'
                : 'border-gray-500 bg-white transition-colors hover:border-bleu-nuit',
          )}
        />
        {survol ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-md bg-gray-50 text-center">
            <span className="inline-flex shrink-0 text-bleu-nuit">
              <Icon name="download" size={32} />
            </span>
            <span className="font-bold text-bleu-nuit text-body-lg leading-snug">
              Déposez le fichier pour l'importer
            </span>
            <span className="font-light text-gray-700 text-small leading-snug">
              son contenu s'affichera dans la zone
            </span>
          </div>
        ) : lectureAffichee ? (
          <output
            id={idLecture}
            // Inside the 2 px frame, which stays visible (dashed when the zone is empty).
            className="pointer-events-none absolute inset-1 flex flex-col items-center justify-center gap-3 rounded-md bg-white text-center"
          >
            <span className="inline-flex text-gray-700">
              <LoadingRing />
            </span>
            <span className="font-semibold text-body text-gray-800 leading-[1.4]">
              Lecture du fichier...
            </span>
            <span className="max-w-[560px] break-words font-light text-gray-600 text-small leading-[1.4]">
              {lecture}
            </span>
          </output>
        ) : vide ? (
          <div
            id={idAide}
            className="pointer-events-none absolute inset-0 flex items-center justify-center gap-12 text-center"
          >
            <div className="flex w-60 flex-col items-center gap-2">
              <span className="inline-flex shrink-0 text-gray-700">
                <Icon name="edit_note" size={32} />
              </span>
              <span className="font-semibold text-body text-gray-800 leading-snug">
                Écrivez ou collez votre texte
              </span>
              <span className="font-light text-gray-600 text-small leading-snug">
                cliquez dans la zone
              </span>
            </div>
            <div
              aria-hidden="true"
              className="my-24 flex flex-col items-center gap-2 self-stretch text-gray-600 text-small"
            >
              <span className="grow border-gray-200 border-l" />
              ou
              <span className="grow border-gray-200 border-l" />
            </div>
            <div className="flex w-60 flex-col items-center gap-2">
              <span className="inline-flex shrink-0 text-gray-700">
                <Icon name="upload_file" size={32} />
              </span>
              <span className="font-semibold text-body text-gray-800 leading-snug">
                Déposez un fichier
              </span>
              <span className="font-light text-gray-600 text-small leading-snug">
                glissez-le dans la zone
              </span>
            </div>
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex min-h-5 flex-wrap items-center gap-x-4 gap-y-1">
        {vide || bloque || tropLong ? null : (
          <span className="inline-flex items-center gap-2 text-caption text-gray-600 leading-snug">
            <Icon name="upload_file" size={16} />
            Vous pouvez aussi déposer un fichier dans la zone
          </span>
        )}
        {tropLong ? (
          <span
            id={idTropLong}
            className="inline-flex items-center gap-2 font-semibold text-rouge-deep text-small leading-snug"
          >
            <Icon name="error" size={20} />
            {MESSAGE_TEXTE_TROP_LONG_ZONE}
          </span>
        ) : null}
        {erreurFichier !== null ? (
          <span
            id={idErreur}
            role="alert"
            className="inline-flex items-center gap-2 font-semibold text-rouge-deep text-small leading-snug"
          >
            <Icon name="error" size={20} />
            {erreurFichier}
          </span>
        ) : null}
        {compteur ? (
          <span
            id={idCompteur}
            className={cn(
              'ml-auto text-caption tabular-nums leading-snug',
              tropLong ? 'font-semibold text-rouge-deep' : 'text-gray-600',
            )}
          >
            {nombre(longueur)} / {nombre(LONGUEUR_MAX_TEXTE)} caractères
          </span>
        ) : null}
      </div>
    </>
  );
}
